import { Trans, t } from "@lingui/macro";
import { useEffect, useReducer, useState } from "react";
import { useHistory } from "../../hooks/useRouterCompat";
import { Soribot } from "../../models/Soribot";
import { Fire } from "../../services/FireClient";
import { UtilApp } from "../../services/Utils/UtilApp";
import ProfileImg from "../components/ProfileImg";
import { browseFile } from "../components/RoomFormPart";
import { ResizeImage } from "../../services/Utils/ImageResizer";
import { UUID } from "../../models/local/Uid";
import { My } from "../../services/My";
import { useAppUIContext } from "../../context/AppUIContext";
import { pojo } from "../../services/Utils/Util";
import { SoribotSubType } from "../../models/local/Types";
import { SoribotDetailsBrokerPart } from "./SoribotDetailsBrokerPart";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Loader2, Trash2, XCircle } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Label } from "../../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

export const SoribotDetails: React.FC<{ bot: Soribot }> = ({ bot }) => {
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const { toast } = useToast();
  const history = useHistory();
  const [botImgUrl, setBotImgUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const { workspaces } = useAppUIContext();
  const [draft, setDraft] = useState<Soribot>(pojo(bot))
  const [inProgress, setInProgress] = useState(false);
  const [hasOpenAIKey, setHasOpenAIKey] = useState<boolean>();
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState(false);

  const workspace = workspaces.find(ws => ws.id === bot.workspaceId);

  let isRetrievalType = false;
  if (mode === 'edit')
    isRetrievalType = !draft.subType || draft.subType === 'retrieval';
  else
    isRetrievalType = !bot.subType || bot.subType === 'retrieval';

  useEffect(() => {
    setMode('view');
    setBotImgUrl(bot.imageUrl ?? null);
    Fire.bot.getOpenAIKeyLast4(bot.workspaceId).then(s => setHasOpenAIKey(!!s));
  }, [bot.id]);

  useEffect(() => {
    setDraft(pojo(bot));
  }, [mode]);

  return <div className="space-y-6">
    {hasOpenAIKey === false && <div className="p-3 bg-destructive/10 text-destructive rounded text-sm"><Trans>The workspace {workspace?.name} is missing OpenAI Key. This bot will not be functional.</Trans></div>}
    {inProgress && <div className="p-4 flex justify-center"><Loader2 className="animate-spin" /></div>}

    <div className="bg-background rounded-lg p-6 shadow-sm border">
      {mode === 'view' && ViewContent()}
      {mode === 'edit' && EditContent()}
    </div>

    <input id="image-input" type='file' accept="image/*" onChange={coverUploadImgSelected} hidden />

    <AlertDialog open={confirmDeleteDialog} onOpenChange={setConfirmDeleteDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle><Trans>Delete Soribot</Trans></AlertDialogTitle>
          <AlertDialogDescription><Trans>Are you sure you want to delete this Soribot?</Trans></AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
          <AlertDialogAction onClick={executeDelete} className="bg-destructive hover:bg-destructive/90"><Trans>Delete</Trans></AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>;

  function ViewContent() {
    return <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold"><Trans>Bot profile</Trans></h2>
        <Button variant="outline" size="sm" onClick={() => setMode('edit')}><Trans>Edit</Trans></Button>
      </div>

      <div className="grid md:grid-cols-[200px_1fr] gap-6 mb-8">
        <div className="flex justify-center md:justify-start">
          <Avatar className="w-32 h-32 text-4xl">
            {botImgUrl ? <ProfileImg url={botImgUrl} className="object-cover" /> :
              <img src={'/assets/img/ai-soribot.png'} className="w-full h-full object-cover" />}
            <AvatarFallback>{bot.name[0]}</AvatarFallback>
          </Avatar>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-4">
            {ViewItem(t`Name`, bot.name)}
            {ViewItem(t`Type`, UtilApp.getSoribotSubType(bot.subType))}
            {ViewItem(t`Workspace`, workspace?.name ?? bot.workspaceId ?? t`None`)}
          </div>
        </div>
      </div>

      {draft.subType === 'broker' && <div className="mb-6"><SoribotDetailsBrokerPart bot={draft} mode={mode} /></div>}

      <div className="space-y-6 divide-y">
        <Section title={t`Description`} content={bot.description || t`None`} />
        <Section title={t`Welcome message`} content={bot.welcomeMessage || t`None`} />

        <div className="pt-4 space-y-4">
          <h3 className="font-bold text-muted-foreground text-sm uppercase"><Trans>Prompt engineering</Trans></h3>
          {!bot.systemInstruction && !bot.promptTemplate && <div className="text-sm"><Trans>Default</Trans></div>}

          {bot.promptTemplate && <div className="space-y-1">
            <div className="text-xs font-bold text-muted-foreground"><Trans>Prompt template</Trans>:</div>
            <div className="bg-muted/50 p-3 rounded text-sm whitespace-pre-wrap font-mono border">{bot.promptTemplate}</div>
          </div>}

          {draft.subType !== 'broker' && <>
            {bot.systemInstruction && <div className="space-y-1 mt-4">
              <div className="text-xs font-bold text-muted-foreground"><Trans>System instruction</Trans>:</div>
              <div className="bg-muted/50 p-3 rounded text-sm whitespace-pre-wrap font-mono border">{bot.systemInstruction}</div>
            </div>}

            <div className="mt-6 pt-6 border-t">
              <h3 className="font-bold text-muted-foreground text-sm uppercase mb-4"><Trans>Other</Trans></h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {ViewItem(t`LLM model`, bot.llmModelName ?? t`None`)}
                {ViewItem(t`Temperature`, bot.llmTemperature?.toString() ?? '0')}
                {isRetrievalType && ViewItem(t`Nr of chunks`, bot.useNoChunks?.toString() ?? '3')}
                {ViewItem(t`Embedding model`, bot.embeddingModel ?? 'text-embedding-ada-002')}
              </div>
            </div>
          </>}
        </div>

        <div className="pt-4 flex justify-between text-xs text-muted-foreground">
          <span><Trans>Created: {UtilApp.dateStrToDay(bot.created, My.user.lang)}</Trans></span>
          <span><Trans>Owner: {bot.owner.name}</Trans></span>
        </div>
      </div>
    </>;
  }

  function Section({ title, content }: { title: string, content: string }) {
    return <div className="pt-4 space-y-2">
      <h3 className="font-bold text-muted-foreground text-sm uppercase">{title}</h3>
      <p className="text-sm text-foreground whitespace-pre-wrap">{content}</p>
    </div>
  }

  function ViewItem(label: string, value: string) {
    return <div className="flex flex-col gap-1">
      <span className="text-xs font-bold text-muted-foreground">{label}:</span>
      <span className="text-sm text-foreground truncate">{value}</span>
    </div>
  }

  function EditContent() {
    return <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold"><Trans>Bot profile</Trans></h2>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={cancelChanges}><Trans>Cancel</Trans></Button>
          <Button onClick={saveChanges} disabled={uploading || inProgress}><Trans>Save changes</Trans></Button>
        </div>
      </div>

      <div className="grid md:grid-cols-[200px_1fr] gap-6 mb-8">
        <div className="flex flex-col items-center">
          <div className="relative group cursor-pointer" onClick={browseFile}>
            <Avatar className="w-32 h-32 text-4xl border-2 border-transparent group-hover:border-primary transition-all">
              {botImgUrl ? <ProfileImg url={botImgUrl} className="object-cover" /> :
                <img src='/assets/img/ai-soribot.png' className="w-full h-full object-cover" />}
            </Avatar>
            {uploading && <div className="absolute inset-0 flex items-center justify-center bg-on-media rounded-full"><Loader2 className="animate-spin text-overlay-control-text" /></div>}
            {!uploading && <div className="absolute inset-0 flex items-center justify-center bg-overlay-control text-overlay-control-text opacity-0 group-hover:opacity-100 rounded-full transition-opacity text-xs font-medium"><Trans>Change</Trans></div>}
          </div>
          {botImgUrl && <Button variant="ghost" size="sm" className="mt-2 text-destructive hover:text-destructive h-8 text-xs" onClick={(e) => { e.stopPropagation(); clearImage(); }}>
            <XCircle className="w-4 h-4 mr-1" /> <Trans>Remove</Trans>
          </Button>}
        </div>

        <div className="space-y-4 max-w-md">
          <div className="space-y-2">
            <Label><Trans>Name</Trans></Label>
            <Input value={draft.name} onChange={e => { draft.name = e.target.value; forceUpdate(); }} maxLength={35} />
          </div>

          <div className="space-y-2">
            <Label><Trans>Type</Trans></Label>
            <Select value={draft.subType ?? 'retrieval'} onValueChange={(val) => changeType(val as SoribotSubType)}>
              <SelectTrigger><SelectValue placeholder={t`Select type`} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general"><Trans>General Bot</Trans></SelectItem>
                <SelectItem value="retrieval"><Trans>Knowledge Retrieval</Trans></SelectItem>
                <SelectItem value="broker"><Trans>Broker Bot</Trans></SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label><Trans>Workspace</Trans></Label>
            <Select value={draft.workspaceId} onValueChange={(val) => { draft.workspaceId = val; forceUpdate(); }}>
              <SelectTrigger><SelectValue placeholder={t`Select workspace`} /></SelectTrigger>
              <SelectContent>
                {workspace && <SelectItem value={workspace.id}>{workspace.name}</SelectItem>}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <hr className="my-6" />

      {draft.subType === 'broker' && <div className="mb-6"><SoribotDetailsBrokerPart bot={draft} mode={mode} /></div>}

      <div className="space-y-6">
        <div className="space-y-2">
          <Label><Trans>Description</Trans></Label>
          <Textarea value={draft.description || ""} onChange={e => { draft.description = e.target.value; forceUpdate(); }} className="min-h-[100px]" />
        </div>

        <div className="space-y-2">
          <Label><Trans>Welcome message</Trans></Label>
          <Textarea value={draft.welcomeMessage || ""} onChange={e => { draft.welcomeMessage = e.target.value; forceUpdate(); }} className="min-h-[100px]" />
        </div>

        <hr className="my-6" />

        <div className="space-y-4">
          <h3 className="font-bold text-muted-foreground text-sm uppercase"><Trans>Prompt engineering</Trans></h3>
          <div className="space-y-2">
            <Label><Trans>Prompt template</Trans></Label>
            <Textarea value={draft.promptTemplate || getDefaultPrompt(draft.subType)} onChange={e => { draft.promptTemplate = e.target.value; forceUpdate(); }} className="min-h-[200px] font-mono text-sm" />
            <div className="text-xs text-muted-foreground whitespace-pre-wrap">{getPromptHelptext(draft.subType)}</div>
          </div>

          {draft.subType !== 'broker' && <>
            <div className="space-y-2">
              <Label><Trans>System instruction</Trans></Label>
              <Textarea value={draft.systemInstruction || ""} onChange={e => { draft.systemInstruction = e.target.value; forceUpdate(); }} className="min-h-[150px] font-mono text-sm" />
            </div>

            <hr className="my-6" />

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label><Trans>OpenAI LLM model</Trans></Label>
                <Select value={draft.llmModelName} onValueChange={(val) => { draft.llmModelName = val; forceUpdate(); }}>
                  <SelectTrigger><SelectValue placeholder={t`Select LLM model`} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gpt-3.5-turbo">gpt-3.5-turbo</SelectItem>
                    <SelectItem value="gpt-4o">gpt-4o</SelectItem>
                    <SelectItem value="gpt-4-turbo">gpt-4</SelectItem>
                    <SelectItem value="gpt-4">gpt-4</SelectItem>
                    <SelectItem value="gpt-4-turbo-preview">gpt-4-turbo-preview</SelectItem>
                    <SelectItem value="gpt-3.5-turbo-0125">gpt-3.5-turbo-0125</SelectItem>
                    <SelectItem value="gpt-4-1106-preview">gpt-4-1106-preview</SelectItem>
                    <SelectItem value="gpt-3.5-turbo-1106">gpt-3.5-turbo-1106</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label><Trans>Temperature</Trans></Label>
                  <Input type="number" step="0.1" min="0" max="1" value={draft.llmTemperature?.toString() || ""} onChange={e => { draft.llmTemperature = Math.min(1, parseFloat(e.target.value)); forceUpdate(); }} />
                </div>
                {isRetrievalType && <div className="space-y-2">
                  <Label><Trans>Nr of chunks</Trans></Label>
                  <Input type="number" min="1" max="6" value={draft.useNoChunks?.toString() || "3"} onChange={e => { draft.useNoChunks = Math.min(6, Math.max(1, parseInt(e.target.value))); forceUpdate(); }} />
                </div>}
              </div>
            </div>
          </>}
        </div>
      </div>

      <div className="pt-8 flex justify-end">
        <Button variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={deleteBot}>
          <Trash2 className="w-4 h-4 mr-2" /> <Trans>Delete Soribot</Trans>
        </Button>
      </div>
    </>;
  }

  async function coverUploadImgSelected(e: any) {
    if (e.target.files.length) {
      const [_file] = e.target.files;
      const resized = await ResizeImage(_file, 250);
      setBotImgUrl(URL.createObjectURL(resized))
      setUploading(true);
      Fire.uploadFile(resized, `/soribots/${bot.id}/profile/${UUID.get()}.jpg`, url => {
        setBotImgUrl(url);
        setUploading(false);
      }, null);
    }
  }

  function clearImage() {
    setBotImgUrl('');
  }

  async function saveChanges() {
    if (uploading) {
      return;
    }

    draft.imageUrl = botImgUrl;
    draft.promptTemplate = draft.promptTemplate || getDefaultPrompt(draft.subType);
    setInProgress(true);
    await Fire.bot.updateSoribot(draft);
    setInProgress(false);
    setMode('view');
  }

  function cancelChanges() {
    setMode('view');
    setBotImgUrl(bot.imageUrl ?? null);
  }

  function deleteBot() {
    if (bot.owner.id !== My.user.id)
      return toast({ description: t`Only the owner can delete the Soribot` });

    setConfirmDeleteDialog(true);
  }

  async function executeDelete() {
    await Fire.bot.deleteSoribot(bot.id);
    toast({ description: t`Soribot deleted` });
    history.replace(`/ws/${bot.workspaceId}`);
  }

  function changeType(type: SoribotSubType) {
    draft.subType = type;
    draft.promptTemplate = null;
    forceUpdate();
  }
}

function getPromptHelptext(subtype: SoribotSubType | undefined): string {
  const knowledge = '{knowledge}'
  const question = '{question}'
  const prompt = '{prompt}'
  if (!subtype || subtype === 'retrieval')
    return t`You must include ${knowledge} and ${question} in the template.`;
  else if (subtype === 'general')
    return t`You must include ${prompt} in the template.`;
  else
    return '';
}

function getDefaultPrompt(subtype: SoribotSubType | undefined) {
  if (!subtype || subtype === 'retrieval')
    return `Use the following Information to answer the Question at the end in the same language as the Question. 
If the Information given is insufficient for a complete answer, use your best judgment to provide a relevant response.

Information: 
"{knowledge}"

Question:
"{question}"
`;
  else if (subtype === 'broker')
    return `Which of the following Bots should the Question be forwarded to? 

Bots: {bots}

Question: "{question}"

If none of the bots are suitable, please explain politely that the question cannot be answered.
`;
  else
    return "{prompt}";

}
export default SoribotDetails;
